// client/src/api/opportunities.js
// Frontend API service for opportunities (replaces leads.js)



const API_BASE_URL = '/api/opportunities';

/**
 * Fetches all opportunities for the authenticated user
 */
export const getOpportunities = async () => {
    const response = await fetch(API_BASE_URL, {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json'
        },
        credentials: 'include'
    });

    if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to fetch opportunities');
    }

    return response.json();
};

/**
 * Fetches opportunities by status (for pipeline view)
 */
export const getOpportunitiesByStatus = async (status) => {
    const response = await fetch(`${API_BASE_URL}/status/${status}`, {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json'
        },
        credentials: 'include'
    });

    if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to fetch opportunities');
    }

    return response.json();
};

/**
 * Fetches opportunities for a specific client
 */
export const getOpportunitiesByClient = async (clientId) => {
    const response = await fetch(`${API_BASE_URL}/client/${clientId}`, {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json'
        },
        credentials: 'include'
    });

    if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to fetch client opportunities');
    }

    return response.json();
};

/**
 * Fetches a single opportunity by ID
 */
export const getOpportunityById = async (opportunityId) => {
    const response = await fetch(`${API_BASE_URL}/${opportunityId}`, {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json'
        },
        credentials: 'include'
    });

    if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to fetch opportunity');
    }

    return response.json();
};

/**
 * Creates a new opportunity
 */
export const createOpportunity = async (opportunityData) => {
    const response = await fetch(API_BASE_URL, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify(opportunityData)
    });

    if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to create opportunity');
    }

    return response.json();
};

/**
 * Updates an existing opportunity
 */
export const updateOpportunity = async (opportunityId, updateData) => {
    const response = await fetch(`${API_BASE_URL}/${opportunityId}`, {
        method: 'PUT',
        headers: {
            'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify(updateData)
    });

    if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to update opportunity');
    }

    return response.json();
};

/**
 * Updates opportunity status (for drag-and-drop in pipeline)
 */
export const updateOpportunityStatus = async (opportunityId, status) => {
    const response = await fetch(`${API_BASE_URL}/${opportunityId}/status`, {
        method: 'PATCH',
        headers: {
            'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({ status })
    });

    if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to update opportunity status');
    }

    return response.json();
};

/**
 * Links a quote to an opportunity
 */
export const linkQuoteToOpportunity = async (opportunityId, quoteId) => {
    const response = await fetch(`${API_BASE_URL}/${opportunityId}/link-quote`, {
        method: 'PATCH',
        headers: {
            'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({ quoteId })
    });

    if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to link quote');
    }

    return response.json();
};

/**
 * Deletes an opportunity
 */
export const deleteOpportunity = async (opportunityId) => {
    const response = await fetch(`${API_BASE_URL}/${opportunityId}`, {
        method: 'DELETE',
        headers: {
            'Content-Type': 'application/json'
        },
        credentials: 'include'
    });

    if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to delete opportunity');
    }

    return response.json();
};

/**
 * Gets pipeline statistics (for dashboard)
 */
export const getPipelineStats = async () => {
    const response = await fetch(`${API_BASE_URL}/stats/pipeline`, {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json'
        },
        credentials: 'include'
    });

    if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to fetch pipeline stats');
    }

    return response.json();
};

/**
 * Gets conversion metrics (for analytics/dashboard)
 */
export const getConversionMetrics = async () => {
    const response = await fetch(`${API_BASE_URL}/stats/conversion`, {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json'
        },
        credentials: 'include'
    });

    if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to fetch conversion metrics');
    }

    return response.json();
};
